// src/components/logistique/formatLogistique.js
// Libellés, montants, dates et phrases de l'historique du parc.
// Les dates arrivent mises en forme par le serveur (« 2026-11-02 »,
// « 2026-11-02 14:05 ») : elles sont découpées en texte, jamais confiées à un
// `Date` JS, qui pourrait les décaler d'un jour selon le fuseau.

export const ETATS = {
  neuf: { libelle: 'Neuf', teinte: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  bon: { libelle: 'Bon état', teinte: 'border-gray-200 bg-gray-50 text-gray-700' },
  usage: { libelle: 'Usagé', teinte: 'border-amber-200 bg-amber-50 text-amber-800' },
  en_panne: { libelle: 'En panne', teinte: 'border-orange-200 bg-orange-50 text-orange-800' },
  hors_service: { libelle: 'Hors service', teinte: 'border-red-200 bg-red-50 text-red-700' }
};

export const MOTIFS_SORTIE = {
  reforme: 'Réformé',
  perdu: 'Perdu',
  vole: 'Volé',
  donne: 'Donné',
  vendu: 'Vendu'
};

export const libelleEtat = (cle) => ETATS[cle]?.libelle || cle || '-';

// Inventaires (lot 2)
export const JUSTIFICATIONS = {
  reparation: 'En réparation',
  mission: 'En mission avec son détenteur',
  pret: 'Prêté',
  perdu: 'Perdu',
  vole: 'Volé',
  autre: 'Autre raison'
};

export const STATUTS_LIGNE = {
  a_voir: { libelle: 'À voir', teinte: 'border-gray-200 bg-white text-gray-600' },
  vu: { libelle: 'Vu', teinte: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  justifie: { libelle: 'Justifié', teinte: 'border-amber-200 bg-amber-50 text-amber-800' },
  manquant: { libelle: 'Manquant', teinte: 'border-red-200 bg-red-50 text-red-700' }
};

export const montantFCFA = (valeur) => (valeur === null || valeur === undefined || valeur === ''
  ? '-'
  : `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(valeur) || 0))} FCFA`);

export const nombre = (valeur) => new Intl.NumberFormat('fr-FR').format(Number(valeur) || 0);

export const pluriel = (n, singulier, plurielForme = `${singulier}s`) => `${nombre(n)} ${Number(n) > 1 ? plurielForme : singulier}`;

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « 2026-11-02 » ou « 2026-11-02 14:05 » → « 2 novembre 2026 » (+ « à 14:05 »). */
export function dateLisible(texte, { avecHeure = true } = {}) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(texte || ''));
  if (!m) return '-';
  const [, a, mo, j, h, mi] = m;
  const jour = Number(j) === 1 ? '1er' : String(Number(j));
  const base = `${jour} ${MOIS[Number(mo) - 1] || ''} ${a}`;
  return avecHeure && h ? `${base} à ${h}:${mi}` : base;
}

/** « 2026-11-02 » → « 02/11/2026 », pour une colonne de tableau. */
export function dateCourte(texte) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(texte || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '-';
}

/** La ligne d'historique, en une phrase. */
export function phraseMouvement(m) {
  const d = m.details || {};
  switch (m.type) {
    case 'creation':
      return `Entré au registre${d.responsable ? `, sous la responsabilité de ${d.responsable}` : ''}${d.lieu ? `, rangé à « ${d.lieu} »` : ''}.`;
    case 'modification': {
      const champs = Array.isArray(d.champs) ? d.champs.map((c) => c.libelle).filter(Boolean) : [];
      return champs.length ? `Fiche modifiée : ${champs.join(', ').toLowerCase()}.` : 'Fiche modifiée.';
    }
    case 'remise':
      return `Remis à ${m.employe || d.detenteur || 'une personne'}.`;
    case 'retour':
      return `Rendu par ${m.employe || d.detenteur || 'son détenteur'}${d.lieu_apres ? `, rangé à « ${d.lieu_apres} »` : ''}.`;
    case 'etat':
      return `État : ${libelleEtat(d.avant)} - ${libelleEtat(d.apres)}.`;
    case 'lieu':
      return d.inventaire
        ? `Trouvé à « ${d.apres || '-'} » à l'inventaire n° ${d.inventaire}${d.avant ? ` (attendu à « ${d.avant} »)` : ''} : lieu corrigé.`
        : `Déplacé${d.avant ? ` de « ${d.avant} »` : ''} à « ${d.apres || '-'} ».`;
    case 'responsable':
      return `Responsable : ${d.avant || 'aucun'} - ${d.apres || m.employe || '-'}.`;
    case 'photo': {
      const ou = d.inventaire ? ` à l'inventaire n° ${d.inventaire}` : '';
      return d.apres ? `Photo prise${ou}, état : ${libelleEtat(d.apres)}.` : `Photo prise${ou}.`;
    }
    case 'impression':
      return `Étiquette imprimée${d.code ? ` (${d.code})` : ''}.`;
    case 'etiquette':
      return `Étiquette remplacée : ${d.avant || '-'} ne vaut plus, ${d.apres || '-'} la remplace.`;
    case 'sortie':
      return `Sorti du parc : ${(MOTIFS_SORTIE[d.motif] || d.motif || '').toLowerCase()}${d.detenteur ? `, il était chez ${d.detenteur}` : ''}.`;
    default:
      return m.type;
  }
}

/** Le détail d'une modification : « Marque : HP - Dell ». */
export function detailsModification(m) {
  if (m.type !== 'modification' || !Array.isArray(m.details?.champs)) return [];
  return m.details.champs
    .filter((c) => c.champ !== 'notes')
    .map((c) => {
      const valeur = (v) => (c.champ === 'valeur_acquisition' && v ? montantFCFA(v) : (c.champ === 'date_acquisition' && v ? dateCourte(v) : (v || 'vide')));
      return `${c.libelle} : ${valeur(c.avant)} - ${valeur(c.apres)}`;
    });
}

export const nomActif = (a) => [a?.designation, [a?.marque, a?.modele].filter(Boolean).join(' ')].filter(Boolean).join(' - ');
